// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { CustomEvent } from "../../Utils/CustomEvents.ts";
import { Rectangle } from "../../Commons/Geometry.ts";
import { ThemeManager } from "../misc/ThemeManager.js";

export let Scroll = function (x, y, width, height, scrollStartIndex, scrollElementsCount, ctx, elementHeight)
{
    this.scrollIndex = scrollStartIndex;
    this.scrollElementsCount = scrollElementsCount;
    this.ctx = ctx;
    this.elementHeight = elementHeight;
    this.isSliderMove = false;
    this.OnValueChange = new CustomEvent();
    this.Visible = false;
    this.maxElementsOnScreen = 0;

    this.Hidden = false;

    this.minY;
    this.maxY;
    this.moveDeltaY;
    this.scaledElementH;

    this.initScrollControls();
    this.setBounds(new Rectangle(x, y, width, height));
};

Scroll.prototype.initScrollControls = function ()
{
    this.btnUp = {};
    this.btnUp.rectangle = new Rectangle(0, 0, Scroll.SCROLL_BTN_WIDTH, Scroll.SCROLL_BTN_HEIGHT);
    this.btnUp.visualState = VisualControlState.None;

    this.btnDown = {};
    this.btnDown.rectangle = new Rectangle(0, 0, Scroll.SCROLL_BTN_WIDTH, Scroll.SCROLL_BTN_HEIGHT);
    this.btnDown.visualState = VisualControlState.None;

    this.slider = {};
    this.slider.rectangle = new Rectangle(0, 0, Scroll.SCROLL_SLIDER_WIDTH, 0);
    this.slider.visualState = VisualControlState.None;

    this.controls = [this.btnUp, this.slider, this.btnDown];
};

//#region Draw methods

Scroll.prototype.Draw = function ()
{
    if (!this.Visible || this.Hidden)
        return;

    var ctx = this.ctx;
    if (!this.ctx)
        return;
    ctx.save();

    ctx.imageSmoothingEnabled = false;

    var myRect = this.containerRectangle;
    var myUP = this.btnUp.rectangle;
    var myDOWN = this.btnDown.rectangle;

    //clear Place
    ctx.fillStyle = ThemeManager.CurrentTheme.scrollBackgroundColor;
    ctx.fillRect(myRect.X, myRect.Y, myRect.Width, myRect.Height);

    Scroll.drawButtons(
        ctx,
        this.btnUp.rectangle, this.btnUp.visualState,
        this.btnDown.rectangle, this.btnDown.visualState);

    Scroll.drawSlider(ctx, this.slider.rectangle, this.slider.visualState);

    ctx.restore();
};

Scroll.drawButtons = function (ctx, btnPrevRect, btnPrevVisualState, btnNextRect, btnNextVisualState, horizontal)
{
    var rectUp = btnPrevRect;
    var imgUp = undefined;

    switch (btnPrevVisualState)
    {
        case VisualControlState.None:
            imgUp = ThemeManager.CurrentTheme.scrollUpImg;
            break;
        case VisualControlState.Hovered:
            imgUp = ThemeManager.CurrentTheme.scrollUpHoverImg;
            break;
        case VisualControlState.Clicked:
            imgUp = ThemeManager.CurrentTheme.scrollUpClickImg;
            break;
    }

    var rectDown = btnNextRect;
    var imgDown = undefined;

    switch (btnNextVisualState)
    {
        case VisualControlState.None:
            imgDown = ThemeManager.CurrentTheme.scrollDownImg;
            break;
        case VisualControlState.Hovered:
            imgDown = ThemeManager.CurrentTheme.scrollDownHoverImg;
            break;
        case VisualControlState.Clicked:
            imgDown = ThemeManager.CurrentTheme.scrollDownClickImg;
            break;
    }

    if (horizontal)
    {

        var centerX = rectUp.X + rectUp.Width / 2;
        var centerY = rectUp.Y + rectUp.Height / 2;
        ctx.DrawRotatedImage(imgUp, centerX, centerY, -90);
        var centerX = rectDown.X + rectDown.Width / 2;
        var centerY = rectDown.Y + rectDown.Height / 2;
        ctx.DrawRotatedImage(imgDown, centerX, centerY, -90);

    } else
    {
        if (imgUp) ctx.drawImage(imgUp, rectUp.X - 1, rectUp.Y);
        if (imgDown) ctx.drawImage(imgDown, rectDown.X - 1, rectDown.Y);
    }
};

Scroll.drawSlider = function (ctx, rect, visualState)
{
    var drawData = Scroll.getDrawingData(visualState);
    var imgTop = drawData.imgTop;
    var imgBottom = drawData.imgBottom;
    var middleDrawMethod = drawData.middleDrawMethod;

    if (!imgTop || !imgBottom) return;

    var left = rect.X + Scroll.SCROLL_SLIDER_LEFT - 1;

    if (imgTop) ctx.drawImage(imgTop, left, rect.Y);

    var lengthToDraw = rect.Height - imgTop.height - imgBottom.height;
    if (lengthToDraw > 0 && middleDrawMethod)
    {
        middleDrawMethod(
            ctx,
            left,
            rect.Y + imgTop.height,
            lengthToDraw);
    }

    if (imgBottom) ctx.drawImage(imgBottom, left, rect.Y + rect.Height - imgBottom.height);
};

Scroll.drawHorizontalSlider = function (ctx, rect, visualState)
{
    var drawData = Scroll.getDrawingData(visualState);
    var imgTop = drawData.imgTop;
    var imgBottom = drawData.imgBottom;
    var middleDrawMethod = drawData.middleDrawMethod;

    if (!imgTop || !imgBottom) return;

    ctx.save();

    ctx.DrawRotatedImage(imgTop, rect.X + imgTop.height / 2, rect.Y + 9, -90);
    var lengthToDraw = rect.Width - imgTop.height - imgBottom.height;

    if (lengthToDraw > 0 && middleDrawMethod)
    {

        ctx.save();

        var left = rect.X + imgTop.height;
        var top = rect.Y + 12;

        ctx.translate(left, top);
        ctx.RotateByAngle(-90);
        ctx.translate(-left, -top);

        middleDrawMethod(ctx, left, top, lengthToDraw);

        ctx.restore();
    }

    ctx.DrawRotatedImage(imgBottom, rect.X + rect.Width - imgBottom.height / 2, rect.Y + 9, -90);

    ctx.restore();
};

// STATIC.
Scroll.drawMiddleDefaultSection = function (ctx, x, y, height)
{
    Scroll.drawMiddleSection(ctx, x, y, height,
        ThemeManager.CurrentTheme.scrollVertMidMainColor,
        ThemeManager.CurrentTheme.scrollVertMidBorderColor);
};

// STATIC.
Scroll.drawMiddleHoverSection = function (ctx, x, y, height)
{
    Scroll.drawMiddleSection(ctx, x, y, height,
        ThemeManager.CurrentTheme.scrollVertMidHoverMainColor,
        ThemeManager.CurrentTheme.scrollVertMidHoverBorderColor);
};

// STATIC.
Scroll.drawMiddleClickSection = function (ctx, x, y, height)
{
    Scroll.drawMiddleSection(ctx, x, y, height,
        ThemeManager.CurrentTheme.scrollVertMidClickMainColor,
        ThemeManager.CurrentTheme.scrollVertMidClickBorderColor);
};

// STATIC.
Scroll.drawMiddleSection = function (ctx, x, y, height, mainColor, borderColor)
{
    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = mainColor;
    ctx.fillRect(x, y - 0.5, Scroll.SCROLL_SLIDER_WIDTH, height + 1);

    // Sharp lines.
    // ctx.translate(0.5, 0.5);

    // ctx.strokeStyle = borderColor;
    // ctx.lineWidth = 1;

    // ctx.beginPath();

    // var top = y - 1;
    // var bottom = y + height;

    // ctx.moveTo(x, top);
    // ctx.lineTo(x, bottom);

    // x += 9;

    // ctx.moveTo(x, top);
    // ctx.lineTo(x, bottom);

    ctx.stroke();

    ctx.restore();
}

// STATIC.
Scroll.getDrawingData = function (visualState)
{
    var imgTop = null;
    var imgBottom = null;
    var middleDrawMethod = null;

    switch (visualState)
    {
        case VisualControlState.None:
            imgTop = ThemeManager.CurrentTheme.scrollVertTopImg;
            imgBottom = ThemeManager.CurrentTheme.scrollVertBotImg;
            middleDrawMethod = Scroll.drawMiddleDefaultSection;
            break;
        case VisualControlState.Hovered:
            imgTop = ThemeManager.CurrentTheme.scrollVertTopHoverImg;
            imgBottom = ThemeManager.CurrentTheme.scrollVertBotHoverImg;
            middleDrawMethod = Scroll.drawMiddleHoverSection;
            break;
        case VisualControlState.Clicked:
            imgTop = ThemeManager.CurrentTheme.scrollVertTopClickImg;
            imgBottom = ThemeManager.CurrentTheme.scrollVertBotClickImg;
            middleDrawMethod = Scroll.drawMiddleClickSection;
            break;
    }

    return { imgTop: imgTop, imgBottom: imgBottom, middleDrawMethod: middleDrawMethod };
}

//#endregion

Scroll.prototype.setScrollElementsCount = function (newCount)
{
    if (this.scrollElementsCount === newCount)
        return;

    this.scrollElementsCount = newCount;
    this.updateScrollControlsData();
};

Scroll.prototype.setBounds = function (rect)
{
    if (!rect) return;

    var cRect = this.containerRectangle;
    if (cRect && cRect.Equals(rect)) return;

    this.containerRectangle = rect;
    this.updateScrollControlsData();
};

Scroll.prototype.updateScrollControlsData = function ()
{
    var cRect = this.containerRectangle;
    var elementsTotal = this.scrollElementsCount;

    // Count only entirely visible elements.
    var maxElementsOnScreen = Math.floor(this.containerRectangle.Height / this.elementHeight);

    var allElementsFitScreen = elementsTotal <= maxElementsOnScreen;

    var availableH = cRect.Height - 2 * Scroll.SCROLL_BTN_HEIGHT;

    var scaledElementH = elementsTotal ?
        availableH / elementsTotal :
        1;

    var sliderH = allElementsFitScreen ?
        availableH :
        maxElementsOnScreen * scaledElementH;

    if (sliderH < Scroll.SCROLL_SLIDER_MIN_HEIGHT)
    {
        sliderH = Scroll.SCROLL_SLIDER_MIN_HEIGHT;
        scaledElementH =
            (availableH - sliderH) /
            (elementsTotal - maxElementsOnScreen);
    }

    var scrollControlLeft = cRect.X + 1;

    var slider = this.slider;
    slider.rectangle.X = scrollControlLeft;
    slider.rectangle.Height = sliderH;

    var btnUpRect = this.btnUp.rectangle;
    btnUpRect.X = scrollControlLeft;
    btnUpRect.Y = cRect.Y;

    var btnDownRect = this.btnDown.rectangle;
    btnDownRect.X = scrollControlLeft;
    btnDownRect.Y = cRect.Height + cRect.Y - Scroll.SCROLL_BTN_HEIGHT;

    this.minY = cRect.Y + Scroll.SCROLL_BTN_HEIGHT;
    this.maxY = cRect.Y + cRect.Height - Scroll.SCROLL_BTN_HEIGHT - sliderH;
    this.scaledElementH = scaledElementH;
    this.maxElementsOnScreen = maxElementsOnScreen;
    this.Visible = !allElementsFitScreen;

    if (!this.Visible && this.scrollIndex !== 0)
        this.moveScrollToElement(0);
    else
        this.moveScrollToElement(this.scrollIndex);
};

Scroll.prototype.trailingScroll = function (leadingIdx)
{
    if (leadingIdx <= this.scrollIndex)
    {
        this.moveScrollToElement(leadingIdx);
        return;
    }

    var bottomScrollIndex = this.scrollIndex + this.maxElementsOnScreen - 1;
    if (this.scrollIndex <= leadingIdx && leadingIdx <= bottomScrollIndex)
        return;

    this.moveScrollToElement(this.scrollIndex + leadingIdx - bottomScrollIndex);
};

Scroll.prototype.correctScrollIndex = function (preferredIndex)
{
    var maxElementsOnScreen = this.maxElementsOnScreen;
    if (maxElementsOnScreen >= this.scrollElementsCount)
        return 0;

    if (preferredIndex < 0)
        return 0;

    if (preferredIndex >= this.scrollElementsCount)
        return this.scrollElementsCount - maxElementsOnScreen - 1;

    var elementsBelowScreen =
        this.scrollElementsCount -
        preferredIndex -
        maxElementsOnScreen;

    if (elementsBelowScreen >= 0)
        return preferredIndex;

    // Shifting scroll index back.
    preferredIndex += elementsBelowScreen;
    if (preferredIndex < 0) preferredIndex = 0;

    return preferredIndex;
};

Scroll.prototype.moveScrollToElement = function (index)
{
    if (index !== this.scrollIndex)
    {
        this.scrollIndex = this.correctScrollIndex(index);
        this.OnValueChange.Raise(this.scrollIndex);
    }

    var newY = this.minY + this.scrollIndex * this.scaledElementH;
    if (newY > this.maxY) newY = this.maxY;
    this.slider.rectangle.Y = newY;

    if (this.Visible) this.Draw();
};

Scroll.prototype.moveSliderToY = function (newY)
{
    if (newY < this.minY) newY = this.minY;
    else if (newY > this.maxY) newY = this.maxY;
    this.slider.rectangle.Y = newY;

    this.scrollIndex = Math.round((newY - this.minY) / this.scaledElementH);
    this.OnValueChange.Raise(this.scrollIndex);

    if (this.Visible) this.Draw();
};

Scroll.prototype.mouseWheel = function (isUp)
{
    this.moveScrollToElement(this.scrollIndex + (isUp ? -1 : 1));
};

Scroll.prototype.mouseDown = function (event)
{
    if (!this.Visible)
        return;

    //find part of scroll
    if (this.slider.rectangle.Contains(event.offsetX, event.offsetY))
    {
        this.slider.visualState = VisualControlState.Clicked;
        this.isSliderMove = true;
        this.moveDeltaY = event.offsetY - this.slider.rectangle.Y;
        this.Draw();
    }
    else if (this.btnUp.rectangle.Contains(event.offsetX, event.offsetY))
    {
        this.btnUp.visualState = VisualControlState.Clicked;
        this.mouseWheel(true);
    }
    else if (this.btnDown.rectangle.Contains(event.offsetX, event.offsetY))
    {
        this.btnDown.visualState = VisualControlState.Clicked;
        this.mouseWheel(false);
    }
    else this.moveSliderToY(event.offsetY);
};

Scroll.prototype.mouseMove = function (event)
{
    if (!this.Visible || !this.isSliderMove)
        return;

    this.slider.visualState = VisualControlState.Clicked;
    this.moveSliderToY(event.offsetY - this.moveDeltaY);
};

// TODO. Qtable, qtree call updateControlHoverVisualState.
// For updating hover/default state only.
Scroll.prototype.onMouseMove = function (event)
{
    this.updateControlHoverVisualState(event);
};

Scroll.prototype.mouseUp = function (event)
{
    this.isSliderMove = false;
    // TODO.
    // Corrects slider y position.
    this.moveScrollToElement(this.scrollIndex);
};

Scroll.prototype.updateControlHoverVisualState = function (event)
{
    if (!this.Visible)
        return;

    var controls = this.controls;
    var len = controls.length;
    for (var i = 0; i < len; i++)
    {
        var control = controls[i];
        control.visualState =
            control.rectangle.Contains(event.offsetX, event.offsetY) ?
                VisualControlState.Hovered :
                VisualControlState.None;
    }

    this.Draw();
};

Scroll.prototype.getVisibleCount = function ()
{
    return this.maxElementsOnScreen;
};

Scroll.prototype.themeChange = function () { };

Scroll.SCROLL_BTN_WIDTH = 0;
Scroll.SCROLL_BTN_HEIGHT = 1;
Scroll.SCROLL_WIDTH = 10;
Scroll.SCROLL_SLIDER_WIDTH = 6;
Scroll.SCROLL_SLIDER_LEFT = 2;

Scroll.SCROLL_SLIDER_MIN_HEIGHT = 17;

export const VisualControlState =
{
    None: 0,
    Clicked: 1,
    Hovered: 2
};
Object.freeze(VisualControlState);